import type { Translate } from 'next-translate';
import React from 'react';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { TextWithLinks } from '@/components/elements/TextWithLinks';
import {
	ACCOUNT_SETTINGS_LINK,
	CONSTANTS,
	SUPPORT_MAIL_LINK,
} from '@/constants';
import { type PricingPlan } from '../../data';
import { PricingCta } from '../PricingCta';
import styles from './PricingOffer.module.scss';

/** `nonTier1` is the monetization-test offer, `default` mirrors the Tier-1 paywall */
export type PricingOfferVariant = 'default' | 'nonTier1';

interface Props {
	t: Translate;
	variant: PricingOfferVariant;
	plans: PricingPlan[];
	activePlanId: string;
	/** Geo hasn't answered yet, so the prices on hand may not be the ones to show */
	isLoading?: boolean;
	onSelectPlan: (planId: string) => void;
	onSubmit: () => void;
}

/** One placeholder per plan row, so the card doesn't resize once they arrive */
const PLACEHOLDER_ROWS = [0, 1, 2];

export const PricingOffer: React.FC<Props> = ({
	t,
	variant,
	plans,
	activePlanId,
	isLoading = false,
	onSelectPlan,
	onSubmit,
}) => {
	// Only the non_tier1 card carries its own legal disclosure — the default
	// (Tier-1-mirroring) flow's checkout page already states it there
	const legalLinks = {
		'{terms}': (
			<LinkWithQuery
				href='/subscription-terms'
				className={styles['offer__legal-link']}
				target='_blank'
			>
				{t('plans_legalTerms')}
			</LinkWithQuery>
		),
		'{email}': (
			<a href={SUPPORT_MAIL_LINK} className={styles['offer__legal-link']}>
				{CONSTANTS.SUPPORT_MAIL_ADDRESS}
			</a>
		),
		'{settings}': (
			<a href={ACCOUNT_SETTINGS_LINK} className={styles['offer__legal-link']}>
				{t('plans_legalSettings')}
			</a>
		),
	};

	// Legal copy follows the selected plan, same as the web app's paywalls:
	// intro-offer plans get the trial wording, the rest the plain charge one
	const activePlan = plans.find(plan => plan.id === activePlanId) ?? plans[0];
	const { billing } = activePlan;
	const legalBody = t(
		billing.trialDurationKey ? 'plansLegal_trialBody' : 'plansLegal_chargeBody',
	)
		.replace('{priceToday}', billing.priceToday)
		.replace('{renewPrice}', billing.renewPrice)
		.replace('{period}', t(billing.periodKey))
		.replace(
			'{duration}',
			billing.trialDurationKey ? t(billing.trialDurationKey) : '',
		);
	const legalText = `${legalBody} ${t('plansLegal_tail')}`;

	const isDefault = variant === 'default';

	const renderBadge = (plan: PricingPlan) =>
		plan.badge ? (
			<span
				className={`${styles['offer__badge']} ${
					styles[`offer__badge_${plan.badgeVariant ?? 'primary'}`]
				}`}
			>
				{/* Badges worked out from the prices carry their own figure — local
				    prices are configured per country, so the saving differs by geo */}
				{t(plan.badge).replace('{percent}', plan.badgePercent ?? '')}
			</span>
		) : null;

	return (
		<div className={`${styles['offer']} ${styles[`offer_${variant}`]}`}>
			<h2 className={styles['offer__title']}>
				{t(isDefault ? 'plansDefault_title' : 'plans_title')}
			</h2>

			{/* The plan set and its currency both follow the visitor's country, which
			    is looked up asynchronously — rather than quote USD and repaint into
			    the local currency a moment later, the rows wait here */}
			{isLoading ? (
				<div
					className={styles['offer__plans']}
					role='status'
					aria-busy='true'
					aria-label={t('plans_loading')}
				>
					{PLACEHOLDER_ROWS.map(row => (
						<span key={row} className={styles['offer__plan-placeholder']} />
					))}
				</div>
			) : (
				<div className={styles['offer__plans']} role='radiogroup'>
					{plans.map(plan => {
						const isActive = plan.id === activePlanId;

						return (
							<button
								key={plan.id}
								type='button'
								role='radio'
								aria-checked={isActive}
								className={styles['offer__plan']}
								data-active={isActive || undefined}
								onClick={() => onSelectPlan(plan.id)}
							>
								{!isDefault && renderBadge(plan)}

								<span className={styles['offer__plan-content']}>
									<span
										className={styles['offer__radio']}
										data-active={isActive || undefined}
									/>

									<span className={styles['offer__plan-info']}>
										<span className={styles['offer__plan-label']}>
											{t(plan.label)}
										</span>

										{plan.note && (
											<span className={styles['offer__plan-note']}>
												{t('plansDefault_nowThenRenews').replace(
													'{price}',
													plan.note.price,
												)}
												<br />
												{t(plan.note.renewKey).replace(
													'{price}',
													plan.note.renewPrice,
												)}
											</span>
										)}
									</span>

									<span className={styles['offer__price-block']}>
										<span className={styles['offer__price']}>{plan.price}</span>
										{plan.suffix && (
											<span className={styles['offer__suffix']}>
												{t(plan.suffix)}
											</span>
										)}
										{!isDefault && plan.priceOld && (
											<span className={styles['offer__price-old']}>
												{plan.priceOld}
											</span>
										)}
									</span>
								</span>
							</button>
						);
					})}
				</div>
			)}

			{/* Follows the selected plan, so it waits on the rows */}
			{variant === 'nonTier1' && !isLoading && (
				<p className={styles['offer__legal']}>
					<TextWithLinks text={legalText} parts={legalLinks} />
				</p>
			)}

			<div className={styles['offer__actions']}>
				{/* Nothing to hand the checkout until the plan set is settled */}
				<PricingCta t={t} disabled={isLoading} onClick={onSubmit} />
			</div>
		</div>
	);
};
